import React, { useCallback, useRef, useState } from "react"
import { FileText } from "lucide-react"
import { ReactComponent as MaterialsEmptyIllustration } from "../../../assets/svg/materials-empty-state.svg"
import styles from "./MaterialsQuotationWelcome.module.scss"

export const MATERIALS_QUOTATION_FILE_INPUT_ID = "materials-quotation-file-input"

const ACCEPT =
  "application/pdf,image/*,.pdf,.png,.jpg,.jpeg,.webp,.xlsx,.xls,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"

type MaterialsQuotationWelcomeBlockProps = {
  isLoading: boolean
  onBeginManual: () => void
  onFileChosen: (file: File) => void
  title?: string
  description?: string
  manualTitle?: string
  manualBullets?: string[]
  manualCta?: string
  assistedTitle?: string
  assistedBadge?: string
  assistedBullets?: string[]
  assistedCta?: string
  assistedHint?: string
  assistedDisabled?: boolean
  onAssistedUnavailableClick?: () => void
  onGoToCostEstimation?: () => void
}

const MaterialsQuotationWelcomeBlock: React.FC<MaterialsQuotationWelcomeBlockProps> = ({
  isLoading,
  onBeginManual,
  onFileChosen,
  title = "Armá tu listado de materiales",
  description = "Elegí cómo arrancar: manual o asistido con IA. Después publicamos tu lista para recibir presupuestos en Edify y notificamos a proveedores relevantes según la zona indicada.",
  manualTitle = "Paso a paso",
  manualBullets = [
    "Buscá por nombre o elegí una categoría para empezar rápido.",
    "Agregá materiales del catálogo o creá materiales personalizados.",
    "Ajustá cantidades y revisá todo antes de publicar.",
  ],
  manualCta = "Cargar manualmente",
  assistedTitle = "Desde un archivo",
  assistedBadge = "IA",
  assistedBullets = [
    "Detectamos materiales y cantidades del archivo cuando el contenido lo permite.",
    "Te mostramos una primera lista para que arranques más rápido.",
    "Antes de publicar, podés ajustar la lista: agregar, quitar o cambiar cantidades.",
  ],
  assistedCta = "Elegir archivo",
  assistedHint = "O soltá el archivo en esta zona",
  assistedDisabled = false,
  onAssistedUnavailableClick,
  onGoToCostEstimation,
}) => {
  const fileRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)

  const selectFile = useCallback(
    (f: File | null | undefined) => {
      if (!f) return
      onFileChosen(f)
    },
    [onFileChosen]
  )

  return (
    <div className={styles.welcomeBlock}>
      <div className={styles.welcomeLayout}>
        <div className={styles.welcomeContent}>
          <div className={styles.materialsWelcomeTop}>
            <div className={styles.materialsWelcomeIntro}>
              <h2 className={styles.welcomeTitle}>{title}</h2>
              <p className={styles.welcomeText}>{description}</p>
            </div>
            <MaterialsEmptyIllustration className={styles.materialsWelcomeIllustration} aria-hidden />
          </div>
          <div className={`${styles.welcomeChoices} ${styles.welcomeChoicesSplit} ${styles.materialsWelcomeChoices}`}>
            <div className={styles.welcomeChoiceCol}>
              <h3 className={styles.welcomeChoiceLabel}>{manualTitle}</h3>
              <ul className={styles.welcomeAgentList}>
                {manualBullets.map((item, idx) => (
                  <li key={`manual-${idx}`}>{item}</li>
                ))}
              </ul>
              <button
                type="button"
                className={styles.welcomeButtonOutline}
                onClick={onBeginManual}
                disabled={isLoading}
              >
                {manualCta}
              </button>
            </div>
            <div className={styles.welcomeChoiceCol}>
              <h3 className={styles.welcomeChoiceLabel}>
                {assistedTitle} <span className={styles.materialsIaBadge}>{assistedBadge}</span>
              </h3>
              <ul className={styles.welcomeAgentList}>
                {assistedBullets.map((item, idx) => (
                  <li key={`assisted-${idx}`}>{item}</li>
                ))}
              </ul>
              <div
                className={`${styles.welcomeAgentDrop} ${dragOver ? styles.welcomeAgentDropActive : ""} ${assistedDisabled ? styles.welcomeAgentDropDisabled : ""}`}
                onDragOver={(e) => {
                  if (assistedDisabled) return
                  e.preventDefault()
                  e.stopPropagation()
                  setDragOver(true)
                }}
                onDragLeave={(e) => {
                  if (assistedDisabled) return
                  e.preventDefault()
                  e.stopPropagation()
                  setDragOver(false)
                }}
                onDrop={(e) => {
                  if (assistedDisabled) return
                  e.preventDefault()
                  e.stopPropagation()
                  setDragOver(false)
                  const f = e.dataTransfer.files?.[0]
                  if (f) selectFile(f)
                }}
              >
                <input
                  ref={fileRef}
                  id={MATERIALS_QUOTATION_FILE_INPUT_ID}
                  type="file"
                  accept={ACCEPT}
                  className={styles.welcomeAssistedFileInput}
                  tabIndex={-1}
                  onChange={(ev) => {
                    const f = ev.target.files?.[0] ?? null
                    ev.target.value = ""
                    selectFile(f ?? undefined)
                  }}
                />
                <FileText size={28} strokeWidth={1.75} className={styles.welcomeAgentDropIcon} aria-hidden />
                <button
                  type="button"
                  className={styles.welcomeButton}
                  onClick={() => {
                    if (assistedDisabled) {
                      onAssistedUnavailableClick?.()
                      return
                    }
                    fileRef.current?.click()
                  }}
                  disabled={isLoading}
                >
                  {assistedCta}
                </button>
                <span className={styles.welcomeAgentDropHint}>{assistedHint}</span>
              </div>
            </div>
          </div>
          {onGoToCostEstimation && (
            <button
              type="button"
              className={styles.welcomeSecondaryLink}
              onClick={onGoToCostEstimation}
              disabled={isLoading}
            >
              ¿Querés una referencia rápida? Ir a estimación de costos
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default MaterialsQuotationWelcomeBlock
